import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PriceCopy, PaymentsWrapper, PaymentsItem, Divider } from './PricingOptions.styles';
import { CopyTwo } from '../../../Common/Common.styles';
import { COLORS, Color } from '../../../../styles/variables';
import { PromotionType } from '../../../../types/offers';
import { priceFormat } from '../../../../utils/formatPrice';

type PaymentsOptionsProps = {
  offer: PromotionType;
  offerType: string;
  hasSmallFont?: boolean;
  labelColor?: Color;
};

const PaymentsOptions: React.FC<PaymentsOptionsProps> = ({
  offer,
  offerType,
  hasSmallFont,
  labelColor = COLORS.grey80,
}) => {
  const { monthlyPayment, term, customerDownPayment, aprRate, totalDueAtSign } = offer;
  const { t } = useTranslation();
  const columnGap = useMemo(() => {
    if (offerType === 'lease') return 16;
    return 28;
  }, [offerType]);

  const renderLeaseContent = useMemo(() => {
    return (
      <>
        <PaymentsItem>
          <PriceCopy smallFont={hasSmallFont} isBold>
            {priceFormat(Number(monthlyPayment.toFixed()))}
          </PriceCopy>
          <CopyTwo color={labelColor}>{t('promo.paymentOptions.monthlyPayment')}</CopyTwo>
        </PaymentsItem>
        <Divider />
        <PaymentsItem>
          <PriceCopy smallFont={hasSmallFont} isBold>
            {term}
          </PriceCopy>
          <CopyTwo color={labelColor}>{t('promo.paymentOptions.months')}</CopyTwo>
        </PaymentsItem>
        <Divider />
        <PaymentsItem>
          <PriceCopy data-testid="payment-options-downPayment" smallFont={hasSmallFont} isBold>
            {priceFormat(totalDueAtSign)}
          </PriceCopy>
          <CopyTwo data-testid="downpaymentLabel" color={labelColor}>
            {t('promo.paymentOptions.downPayment')}
          </CopyTwo>
        </PaymentsItem>
      </>
    );
  }, [totalDueAtSign, hasSmallFont, labelColor, monthlyPayment, t, term]);

  const renderFinanceContent = useMemo(() => {
    return (
      <>
        <PaymentsItem>
          <PriceCopy smallFont={hasSmallFont} isBold>
            {(aprRate * 100).toFixed(2)}%
          </PriceCopy>
          <CopyTwo color={labelColor}>{t('promo.paymentOptions.apr')}</CopyTwo>
        </PaymentsItem>
        <Divider />
        <PaymentsItem>
          <PriceCopy smallFont={hasSmallFont} isBold>
            {term}
          </PriceCopy>
          <CopyTwo color={labelColor}>{t('promo.paymentOptions.months')}</CopyTwo>
        </PaymentsItem>
      </>
    );
  }, [aprRate, hasSmallFont, labelColor, t, term]);

  if (!offer) return null;
  if (offerType === 'credit') return null;

  return (
    <PaymentsWrapper columnGap={columnGap}>
      {offerType === 'lease' ? renderLeaseContent : renderFinanceContent}
    </PaymentsWrapper>
  );
};

export default PaymentsOptions;
